import * as z from 'zod';

export const CalculateServiceFeesRequest = z.object({
  order: z
    .object({
      _id: z.string().describe('Order ID.').optional(),
      locationId: z
        .string()
        .describe("ID of the site's location.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      currency: z.string().describe('Currency used for pricing on the site.'),
      priceSummary: z
        .object({
          subtotal: z.string().describe('Subtotal of the order.').optional(),
        })
        .describe('Information about the price of the order.'),
      shippingInfo: z
        .object({
          logistics: z
            .object({
              type: z
                .enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'PICKUP',
                  'DELIVERY',
                  'DINE_IN',
                  'CURBSIDE_PICKUP',
                ])
                .optional(),
            })
            .describe(
              'Information about the type of delivery. For example, pick-up.'
            )
            .optional(),
        })
        .describe("Order's shipping information.")
        .optional(),
      platform: z
        .object({
          value: z.enum(['SITE', 'MOBILE_SITE', 'MOBILE_APP']).optional(),
        })
        .describe('Platform on which the order was placed.')
        .optional(),
      locale: z
        .object({
          languageCode: z
            .string()
            .describe(
              'Locale in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          country: z
            .string()
            .describe(
              '2-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
            )
            .optional()
            .nullable(),
        })
        .describe("Order's locale.")
        .optional(),
    })
    .describe(
      'Order information needed to evaluate the rules and calculate the relevant fees.'
    ),
  options: z
    .object({
      label: z
        .string()
        .describe('DEPRECATED. Defines the app that the rule is connected to.')
        .max(100)
        .min(1)
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe('Defines the app that the rule is connected to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CalculateServiceFeesResponse = z.object({
  calculatedFees: z
    .array(
      z.object({
        ruleId: z
          .string()
          .describe('The ID of the rule that was used to calculate the fee.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z
          .string()
          .describe('The name of the rule that was used to calculate the fee.')
          .optional(),
        fee: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            formattedValue: z
              .string()
              .describe(
                'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Fee amount.')
          .optional(),
        tax: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            formattedValue: z
              .string()
              .describe(
                'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Tax amount.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            'Tax group ID. This is an alternative to calculating the tax amount manually.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const CreateRuleRequest = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    rule: z
      .intersection(
        z.object({
          _id: z.string().describe('Rule ID.').optional().nullable(),
          locationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z.string().describe('Rule name.').max(50).min(1),
          _createdDate: z
            .date()
            .describe('Date and time the rule was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the rule was updated.')
            .optional()
            .nullable(),
          taxRate: z
            .string()
            .describe(
              "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
            )
            .optional()
            .nullable(),
          conditionsType: z
            .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          conditionType: z
            .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          enabled: z
            .boolean()
            .describe(
              'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
            ),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
            )
            .optional()
            .nullable(),
          label: z
            .string()
            .describe(
              'DEPRECATED. Defines the app that the rule is connected to.'
            )
            .max(100)
            .min(1)
            .optional()
            .nullable(),
          appId: z
            .string()
            .describe('Defines the app that the rule is connected to.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          roundingStrategy: z.enum(['HALF_UP', 'HALF_EVEN']).optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Data Extensions')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
            )
            .optional(),
        }),
        z.intersection(
          z.intersection(
            z.intersection(
              z.intersection(
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ]),
                z.xor([
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z.never().optional(),
                  }),
                  z.object({
                    percentageFee: z.never().optional(),
                    fixedFee: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  condition: z.never().optional(),
                  conditionTree: z.never().optional(),
                }),
                z.object({
                  conditionTree: z.never().optional(),
                  condition: z
                    .intersection(
                      z.object({
                        path: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        orderFieldPath: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        expectedType: z
                          .object({
                            value: z
                              .enum(['NUMBER', 'STRING'])
                              .describe(
                                'Type of the field specified in `orderFieldPath`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                        expectedFieldType: z
                          .enum([
                            'UNKNOWN_EXPECTED_FIELD_TYPE',
                            'NUMBER',
                            'STRING',
                          ])
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          number: z.never().optional(),
                          list: z.never().optional(),
                        }),
                        z.object({
                          list: z.never().optional(),
                          number: z
                            .object({
                              value: z
                                .number()
                                .describe(
                                  'Numeric value to compare with the value of the specified field.'
                                )
                                .optional(),
                              operation: z
                                .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                .describe('Operation to use.')
                                .optional(),
                            })
                            .describe(
                              'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                            ),
                        }),
                        z.object({
                          number: z.never().optional(),
                          list: z
                            .object({
                              values: z
                                .array(z.string())
                                .min(1)
                                .max(100)
                                .optional(),
                            })
                            .describe(
                              'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Single condition that must be met for the rule to be applied to an order.'
                    ),
                }),
                z.object({
                  condition: z.never().optional(),
                  conditionTree:
                    comWixpressServiceFeesV1ConditionTreeSchema.describe(
                      'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                    ),
                }),
              ])
            ),
            z.xor([
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions: z.never().optional(),
              }),
              z.object({
                conditionTreeOptions: z.never().optional(),
                conditionOptions: z
                  .intersection(
                    z.object({
                      path: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      orderFieldPath: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      expectedType: z
                        .object({
                          value: z
                            .enum(['NUMBER', 'STRING'])
                            .describe(
                              'Type of the field specified in `orderFieldPath`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                      expectedFieldType: z
                        .enum([
                          'UNKNOWN_EXPECTED_FIELD_TYPE',
                          'NUMBER',
                          'STRING',
                        ])
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        number: z.never().optional(),
                        list: z.never().optional(),
                      }),
                      z.object({
                        list: z.never().optional(),
                        number: z
                          .object({
                            value: z
                              .number()
                              .describe(
                                'Numeric value to compare with the value of the specified field.'
                              )
                              .optional(),
                            operation: z
                              .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                              .describe('Operation to use.')
                              .optional(),
                          })
                          .describe(
                            'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                          ),
                      }),
                      z.object({
                        number: z.never().optional(),
                        list: z
                          .object({
                            values: z
                              .array(z.string())
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Single condition that must be met for the rule to be applied to an order.'
                  ),
              }),
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions:
                  comWixpressServiceFeesV1ConditionTreeSchema.describe(
                    'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z.never().optional(),
            }),
            z.object({
              taxGroupId: z.never().optional(),
              customTaxRate: z
                .string()
                .describe(
                  'Percentage value to apply as a custom tax rate. Range: [0-100].'
                ),
            }),
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z
                .string()
                .describe('Tax group ID. Internal only.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
      )
      .describe('Rule to create.'),
  });
})();
export const CreateRuleResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.intersection(
    z.object({
      _id: z.string().describe('Rule ID.').optional().nullable(),
      locationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Rule name.')
        .max(50)
        .min(1)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the rule was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the rule was updated.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe(
          "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
        )
        .optional()
        .nullable(),
      conditionsType: z
        .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      conditionType: z
        .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      enabled: z
        .boolean()
        .describe(
          'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
        )
        .optional()
        .nullable(),
      label: z
        .string()
        .describe('DEPRECATED. Defines the app that the rule is connected to.')
        .max(100)
        .min(1)
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe('Defines the app that the rule is connected to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      roundingStrategy: z
        .enum(['HALF_UP', 'HALF_EVEN'])
        .describe('Rounding strategy to apply to fee and tax calculation.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
        )
        .optional(),
    }),
    z.intersection(
      z.intersection(
        z.intersection(
          z.intersection(
            z.xor([
              z.object({
                amount: z.never().optional(),
                percentage: z.never().optional(),
              }),
              z.object({
                percentage: z.never().optional(),
                amount: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                amount: z.never().optional(),
                percentage: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ]),
            z.xor([
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z.never().optional(),
              }),
              z.object({
                percentageFee: z.never().optional(),
                fixedFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              condition: z.never().optional(),
              conditionTree: z.never().optional(),
            }),
            z.object({
              conditionTree: z.never().optional(),
              condition: z
                .intersection(
                  z.object({
                    path: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    orderFieldPath: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    expectedType: z
                      .object({
                        value: z
                          .enum(['NUMBER', 'STRING'])
                          .describe(
                            'Type of the field specified in `orderFieldPath`.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                    expectedFieldType: z
                      .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      number: z.never().optional(),
                      list: z.never().optional(),
                    }),
                    z.object({
                      list: z.never().optional(),
                      number: z
                        .object({
                          value: z
                            .number()
                            .describe(
                              'Numeric value to compare with the value of the specified field.'
                            )
                            .optional(),
                          operation: z
                            .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                            .describe('Operation to use.')
                            .optional(),
                        })
                        .describe(
                          'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                        ),
                    }),
                    z.object({
                      number: z.never().optional(),
                      list: z
                        .object({
                          values: z
                            .array(z.string())
                            .min(1)
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Single condition that must be met for the rule to be applied to an order.'
                ),
            }),
            z.object({
              condition: z.never().optional(),
              conditionTree:
                comWixpressServiceFeesV1ConditionTreeSchema.describe(
                  'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                ),
            }),
          ])
        ),
        z.xor([
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions: z.never().optional(),
          }),
          z.object({
            conditionTreeOptions: z.never().optional(),
            conditionOptions: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions:
              comWixpressServiceFeesV1ConditionTreeSchema.describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      ),
      z.xor([
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z.never().optional(),
        }),
        z.object({
          taxGroupId: z.never().optional(),
          customTaxRate: z
            .string()
            .describe(
              'Percentage value to apply as a custom tax rate. Range: [0-100].'
            ),
        }),
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z
            .string()
            .describe('Tax group ID. Internal only.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
  );
})();
export const GetRuleRequest = z.object({
  ruleId: z
    .string()
    .describe('ID of the rule to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetRuleResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.intersection(
    z.object({
      _id: z.string().describe('Rule ID.').optional().nullable(),
      locationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Rule name.')
        .max(50)
        .min(1)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the rule was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the rule was updated.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe(
          "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
        )
        .optional()
        .nullable(),
      conditionsType: z
        .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      conditionType: z
        .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      enabled: z
        .boolean()
        .describe(
          'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
        )
        .optional()
        .nullable(),
      label: z
        .string()
        .describe('DEPRECATED. Defines the app that the rule is connected to.')
        .max(100)
        .min(1)
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe('Defines the app that the rule is connected to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      roundingStrategy: z
        .enum(['HALF_UP', 'HALF_EVEN'])
        .describe('Rounding strategy to apply to fee and tax calculation.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
        )
        .optional(),
    }),
    z.intersection(
      z.intersection(
        z.intersection(
          z.intersection(
            z.xor([
              z.object({
                amount: z.never().optional(),
                percentage: z.never().optional(),
              }),
              z.object({
                percentage: z.never().optional(),
                amount: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                amount: z.never().optional(),
                percentage: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ]),
            z.xor([
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z.never().optional(),
              }),
              z.object({
                percentageFee: z.never().optional(),
                fixedFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              condition: z.never().optional(),
              conditionTree: z.never().optional(),
            }),
            z.object({
              conditionTree: z.never().optional(),
              condition: z
                .intersection(
                  z.object({
                    path: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    orderFieldPath: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    expectedType: z
                      .object({
                        value: z
                          .enum(['NUMBER', 'STRING'])
                          .describe(
                            'Type of the field specified in `orderFieldPath`.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                    expectedFieldType: z
                      .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      number: z.never().optional(),
                      list: z.never().optional(),
                    }),
                    z.object({
                      list: z.never().optional(),
                      number: z
                        .object({
                          value: z
                            .number()
                            .describe(
                              'Numeric value to compare with the value of the specified field.'
                            )
                            .optional(),
                          operation: z
                            .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                            .describe('Operation to use.')
                            .optional(),
                        })
                        .describe(
                          'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                        ),
                    }),
                    z.object({
                      number: z.never().optional(),
                      list: z
                        .object({
                          values: z
                            .array(z.string())
                            .min(1)
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Single condition that must be met for the rule to be applied to an order.'
                ),
            }),
            z.object({
              condition: z.never().optional(),
              conditionTree:
                comWixpressServiceFeesV1ConditionTreeSchema.describe(
                  'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                ),
            }),
          ])
        ),
        z.xor([
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions: z.never().optional(),
          }),
          z.object({
            conditionTreeOptions: z.never().optional(),
            conditionOptions: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions:
              comWixpressServiceFeesV1ConditionTreeSchema.describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      ),
      z.xor([
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z.never().optional(),
        }),
        z.object({
          taxGroupId: z.never().optional(),
          customTaxRate: z
            .string()
            .describe(
              'Percentage value to apply as a custom tax rate. Range: [0-100].'
            ),
        }),
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z
            .string()
            .describe('Tax group ID. Internal only.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
  );
})();
export const UpdateRuleRequest = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    _id: z.string().describe('Rule ID.'),
    rule: z
      .intersection(
        z.object({
          _id: z.string().describe('Rule ID.').optional().nullable(),
          locationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Rule name.')
            .max(50)
            .min(1)
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the rule was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the rule was updated.')
            .optional()
            .nullable(),
          taxRate: z
            .string()
            .describe(
              "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
            )
            .optional()
            .nullable(),
          conditionsType: z
            .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          conditionType: z
            .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          enabled: z
            .boolean()
            .describe(
              'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
            ),
          label: z
            .string()
            .describe(
              'DEPRECATED. Defines the app that the rule is connected to.'
            )
            .max(100)
            .min(1)
            .optional()
            .nullable(),
          appId: z
            .string()
            .describe('Defines the app that the rule is connected to.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          roundingStrategy: z.enum(['HALF_UP', 'HALF_EVEN']).optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Data Extensions')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
            )
            .optional(),
        }),
        z.intersection(
          z.intersection(
            z.intersection(
              z.intersection(
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ]),
                z.xor([
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z.never().optional(),
                  }),
                  z.object({
                    percentageFee: z.never().optional(),
                    fixedFee: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  condition: z.never().optional(),
                  conditionTree: z.never().optional(),
                }),
                z.object({
                  conditionTree: z.never().optional(),
                  condition: z
                    .intersection(
                      z.object({
                        path: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        orderFieldPath: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        expectedType: z
                          .object({
                            value: z
                              .enum(['NUMBER', 'STRING'])
                              .describe(
                                'Type of the field specified in `orderFieldPath`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                        expectedFieldType: z
                          .enum([
                            'UNKNOWN_EXPECTED_FIELD_TYPE',
                            'NUMBER',
                            'STRING',
                          ])
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          number: z.never().optional(),
                          list: z.never().optional(),
                        }),
                        z.object({
                          list: z.never().optional(),
                          number: z
                            .object({
                              value: z
                                .number()
                                .describe(
                                  'Numeric value to compare with the value of the specified field.'
                                )
                                .optional(),
                              operation: z
                                .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                .describe('Operation to use.')
                                .optional(),
                            })
                            .describe(
                              'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                            ),
                        }),
                        z.object({
                          number: z.never().optional(),
                          list: z
                            .object({
                              values: z
                                .array(z.string())
                                .min(1)
                                .max(100)
                                .optional(),
                            })
                            .describe(
                              'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Single condition that must be met for the rule to be applied to an order.'
                    ),
                }),
                z.object({
                  condition: z.never().optional(),
                  conditionTree:
                    comWixpressServiceFeesV1ConditionTreeSchema.describe(
                      'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                    ),
                }),
              ])
            ),
            z.xor([
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions: z.never().optional(),
              }),
              z.object({
                conditionTreeOptions: z.never().optional(),
                conditionOptions: z
                  .intersection(
                    z.object({
                      path: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      orderFieldPath: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      expectedType: z
                        .object({
                          value: z
                            .enum(['NUMBER', 'STRING'])
                            .describe(
                              'Type of the field specified in `orderFieldPath`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                      expectedFieldType: z
                        .enum([
                          'UNKNOWN_EXPECTED_FIELD_TYPE',
                          'NUMBER',
                          'STRING',
                        ])
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        number: z.never().optional(),
                        list: z.never().optional(),
                      }),
                      z.object({
                        list: z.never().optional(),
                        number: z
                          .object({
                            value: z
                              .number()
                              .describe(
                                'Numeric value to compare with the value of the specified field.'
                              )
                              .optional(),
                            operation: z
                              .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                              .describe('Operation to use.')
                              .optional(),
                          })
                          .describe(
                            'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                          ),
                      }),
                      z.object({
                        number: z.never().optional(),
                        list: z
                          .object({
                            values: z
                              .array(z.string())
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Single condition that must be met for the rule to be applied to an order.'
                  ),
              }),
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions:
                  comWixpressServiceFeesV1ConditionTreeSchema.describe(
                    'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z.never().optional(),
            }),
            z.object({
              taxGroupId: z.never().optional(),
              customTaxRate: z
                .string()
                .describe(
                  'Percentage value to apply as a custom tax rate. Range: [0-100].'
                ),
            }),
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z
                .string()
                .describe('Tax group ID. Internal only.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
      )
      .describe('Rule to update.'),
  });
})();
export const UpdateRuleResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.intersection(
    z.object({
      _id: z.string().describe('Rule ID.').optional().nullable(),
      locationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Rule name.')
        .max(50)
        .min(1)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the rule was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the rule was updated.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe(
          "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
        )
        .optional()
        .nullable(),
      conditionsType: z
        .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      conditionType: z
        .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
        .describe('Specifies the type of condition.')
        .optional(),
      enabled: z
        .boolean()
        .describe(
          'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
        )
        .optional()
        .nullable(),
      label: z
        .string()
        .describe('DEPRECATED. Defines the app that the rule is connected to.')
        .max(100)
        .min(1)
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe('Defines the app that the rule is connected to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      roundingStrategy: z
        .enum(['HALF_UP', 'HALF_EVEN'])
        .describe('Rounding strategy to apply to fee and tax calculation.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
        )
        .optional(),
    }),
    z.intersection(
      z.intersection(
        z.intersection(
          z.intersection(
            z.xor([
              z.object({
                amount: z.never().optional(),
                percentage: z.never().optional(),
              }),
              z.object({
                percentage: z.never().optional(),
                amount: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                amount: z.never().optional(),
                percentage: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ]),
            z.xor([
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z.never().optional(),
              }),
              z.object({
                percentageFee: z.never().optional(),
                fixedFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Fixed fee. Must hold a positive value.'),
              }),
              z.object({
                fixedFee: z.never().optional(),
                percentageFee: z
                  .string()
                  .describe(
                    "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              condition: z.never().optional(),
              conditionTree: z.never().optional(),
            }),
            z.object({
              conditionTree: z.never().optional(),
              condition: z
                .intersection(
                  z.object({
                    path: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    orderFieldPath: z
                      .string()
                      .describe(
                        'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                      )
                      .max(1000)
                      .optional(),
                    expectedType: z
                      .object({
                        value: z
                          .enum(['NUMBER', 'STRING'])
                          .describe(
                            'Type of the field specified in `orderFieldPath`.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                    expectedFieldType: z
                      .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                      .describe(
                        'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      number: z.never().optional(),
                      list: z.never().optional(),
                    }),
                    z.object({
                      list: z.never().optional(),
                      number: z
                        .object({
                          value: z
                            .number()
                            .describe(
                              'Numeric value to compare with the value of the specified field.'
                            )
                            .optional(),
                          operation: z
                            .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                            .describe('Operation to use.')
                            .optional(),
                        })
                        .describe(
                          'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                        ),
                    }),
                    z.object({
                      number: z.never().optional(),
                      list: z
                        .object({
                          values: z
                            .array(z.string())
                            .min(1)
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Single condition that must be met for the rule to be applied to an order.'
                ),
            }),
            z.object({
              condition: z.never().optional(),
              conditionTree:
                comWixpressServiceFeesV1ConditionTreeSchema.describe(
                  'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                ),
            }),
          ])
        ),
        z.xor([
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions: z.never().optional(),
          }),
          z.object({
            conditionTreeOptions: z.never().optional(),
            conditionOptions: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            conditionOptions: z.never().optional(),
            conditionTreeOptions:
              comWixpressServiceFeesV1ConditionTreeSchema.describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      ),
      z.xor([
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z.never().optional(),
        }),
        z.object({
          taxGroupId: z.never().optional(),
          customTaxRate: z
            .string()
            .describe(
              'Percentage value to apply as a custom tax rate. Range: [0-100].'
            ),
        }),
        z.object({
          customTaxRate: z.never().optional(),
          taxGroupId: z
            .string()
            .describe('Tax group ID. Internal only.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
  );
})();
export const DeleteRuleRequest = z.object({
  ruleId: z
    .string()
    .describe('ID of the rule to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteRuleResponse = z.object({});
export const ListRulesRequest = z.object({
  options: z
    .object({
      locationId: z
        .string()
        .describe(
          'Retrieve only rule that apply at the [location](https://dev.wix.com/docs/rest/api-reference/business-tools/locations/location-object) with this ID. If this field is `null`, the rules will not be filtered by location.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      label: z
        .string()
        .describe('DEPRECATED. Defines the app that the rule is connected to.')
        .max(100)
        .min(1)
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe('Defines the app that the rule is connected to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListRulesResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    rules: z
      .array(
        z.intersection(
          z.object({
            _id: z.string().describe('Rule ID.').optional().nullable(),
            locationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Rule name.')
              .max(50)
              .min(1)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the rule was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the rule was updated.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe(
                "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
              )
              .optional()
              .nullable(),
            conditionsType: z
              .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
              .describe('Specifies the type of condition.')
              .optional(),
            conditionType: z
              .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
              .describe('Specifies the type of condition.')
              .optional(),
            enabled: z
              .boolean()
              .describe(
                'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^\d+$/, 'Must be a valid UInt64 string')
              .describe(
                'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
              )
              .optional()
              .nullable(),
            label: z
              .string()
              .describe(
                'DEPRECATED. Defines the app that the rule is connected to.'
              )
              .max(100)
              .min(1)
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe('Defines the app that the rule is connected to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            roundingStrategy: z
              .enum(['HALF_UP', 'HALF_EVEN'])
              .describe(
                'Rounding strategy to apply to fee and tax calculation.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
              )
              .optional(),
          }),
          z.intersection(
            z.intersection(
              z.intersection(
                z.intersection(
                  z.xor([
                    z.object({
                      amount: z.never().optional(),
                      percentage: z.never().optional(),
                    }),
                    z.object({
                      percentage: z.never().optional(),
                      amount: z
                        .object({
                          value: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional(),
                          currency: z
                            .string()
                            .describe(
                              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                            )
                            .optional(),
                          formattedValue: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Fixed fee. Must hold a positive value.'),
                    }),
                    z.object({
                      amount: z.never().optional(),
                      percentage: z
                        .string()
                        .describe(
                          "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                        ),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      fixedFee: z.never().optional(),
                      percentageFee: z.never().optional(),
                    }),
                    z.object({
                      percentageFee: z.never().optional(),
                      fixedFee: z
                        .object({
                          value: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional(),
                          currency: z
                            .string()
                            .describe(
                              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                            )
                            .optional(),
                          formattedValue: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Fixed fee. Must hold a positive value.'),
                    }),
                    z.object({
                      fixedFee: z.never().optional(),
                      percentageFee: z
                        .string()
                        .describe(
                          "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    condition: z.never().optional(),
                    conditionTree: z.never().optional(),
                  }),
                  z.object({
                    conditionTree: z.never().optional(),
                    condition: z
                      .intersection(
                        z.object({
                          path: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          orderFieldPath: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          expectedType: z
                            .object({
                              value: z
                                .enum(['NUMBER', 'STRING'])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                          expectedFieldType: z
                            .enum([
                              'UNKNOWN_EXPECTED_FIELD_TYPE',
                              'NUMBER',
                              'STRING',
                            ])
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            number: z.never().optional(),
                            list: z.never().optional(),
                          }),
                          z.object({
                            list: z.never().optional(),
                            number: z
                              .object({
                                value: z
                                  .number()
                                  .describe(
                                    'Numeric value to compare with the value of the specified field.'
                                  )
                                  .optional(),
                                operation: z
                                  .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                  .describe('Operation to use.')
                                  .optional(),
                              })
                              .describe(
                                'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                              ),
                          }),
                          z.object({
                            number: z.never().optional(),
                            list: z
                              .object({
                                values: z
                                  .array(z.string())
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe(
                                'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                              ),
                          }),
                        ])
                      )
                      .describe(
                        'Single condition that must be met for the rule to be applied to an order.'
                      ),
                  }),
                  z.object({
                    condition: z.never().optional(),
                    conditionTree:
                      comWixpressServiceFeesV1ConditionTreeSchema.describe(
                        'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  conditionOptions: z.never().optional(),
                  conditionTreeOptions: z.never().optional(),
                }),
                z.object({
                  conditionTreeOptions: z.never().optional(),
                  conditionOptions: z
                    .intersection(
                      z.object({
                        path: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        orderFieldPath: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        expectedType: z
                          .object({
                            value: z
                              .enum(['NUMBER', 'STRING'])
                              .describe(
                                'Type of the field specified in `orderFieldPath`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                        expectedFieldType: z
                          .enum([
                            'UNKNOWN_EXPECTED_FIELD_TYPE',
                            'NUMBER',
                            'STRING',
                          ])
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          number: z.never().optional(),
                          list: z.never().optional(),
                        }),
                        z.object({
                          list: z.never().optional(),
                          number: z
                            .object({
                              value: z
                                .number()
                                .describe(
                                  'Numeric value to compare with the value of the specified field.'
                                )
                                .optional(),
                              operation: z
                                .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                .describe('Operation to use.')
                                .optional(),
                            })
                            .describe(
                              'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                            ),
                        }),
                        z.object({
                          number: z.never().optional(),
                          list: z
                            .object({
                              values: z
                                .array(z.string())
                                .min(1)
                                .max(100)
                                .optional(),
                            })
                            .describe(
                              'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Single condition that must be met for the rule to be applied to an order.'
                    ),
                }),
                z.object({
                  conditionOptions: z.never().optional(),
                  conditionTreeOptions:
                    comWixpressServiceFeesV1ConditionTreeSchema.describe(
                      'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                    ),
                }),
              ])
            ),
            z.xor([
              z.object({
                customTaxRate: z.never().optional(),
                taxGroupId: z.never().optional(),
              }),
              z.object({
                taxGroupId: z.never().optional(),
                customTaxRate: z
                  .string()
                  .describe(
                    'Percentage value to apply as a custom tax rate. Range: [0-100].'
                  ),
              }),
              z.object({
                customTaxRate: z.never().optional(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID. Internal only.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
        )
      )
      .optional(),
  });
})();
export const QueryRulesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          locationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          appId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['_id', 'locationId', 'appId']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryRulesResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    rules: z
      .array(
        z.intersection(
          z.object({
            _id: z.string().describe('Rule ID.').optional().nullable(),
            locationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Rule name.')
              .max(50)
              .min(1)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the rule was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the rule was updated.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe(
                "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
              )
              .optional()
              .nullable(),
            conditionsType: z
              .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
              .describe('Specifies the type of condition.')
              .optional(),
            conditionType: z
              .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
              .describe('Specifies the type of condition.')
              .optional(),
            enabled: z
              .boolean()
              .describe(
                'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^\d+$/, 'Must be a valid UInt64 string')
              .describe(
                'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
              )
              .optional()
              .nullable(),
            label: z
              .string()
              .describe(
                'DEPRECATED. Defines the app that the rule is connected to.'
              )
              .max(100)
              .min(1)
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe('Defines the app that the rule is connected to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            roundingStrategy: z
              .enum(['HALF_UP', 'HALF_EVEN'])
              .describe(
                'Rounding strategy to apply to fee and tax calculation.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
              )
              .optional(),
          }),
          z.intersection(
            z.intersection(
              z.intersection(
                z.intersection(
                  z.xor([
                    z.object({
                      amount: z.never().optional(),
                      percentage: z.never().optional(),
                    }),
                    z.object({
                      percentage: z.never().optional(),
                      amount: z
                        .object({
                          value: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional(),
                          currency: z
                            .string()
                            .describe(
                              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                            )
                            .optional(),
                          formattedValue: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Fixed fee. Must hold a positive value.'),
                    }),
                    z.object({
                      amount: z.never().optional(),
                      percentage: z
                        .string()
                        .describe(
                          "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                        ),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      fixedFee: z.never().optional(),
                      percentageFee: z.never().optional(),
                    }),
                    z.object({
                      percentageFee: z.never().optional(),
                      fixedFee: z
                        .object({
                          value: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional(),
                          currency: z
                            .string()
                            .describe(
                              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                            )
                            .optional(),
                          formattedValue: z
                            .string()
                            .describe(
                              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Fixed fee. Must hold a positive value.'),
                    }),
                    z.object({
                      fixedFee: z.never().optional(),
                      percentageFee: z
                        .string()
                        .describe(
                          "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    condition: z.never().optional(),
                    conditionTree: z.never().optional(),
                  }),
                  z.object({
                    conditionTree: z.never().optional(),
                    condition: z
                      .intersection(
                        z.object({
                          path: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          orderFieldPath: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          expectedType: z
                            .object({
                              value: z
                                .enum(['NUMBER', 'STRING'])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                          expectedFieldType: z
                            .enum([
                              'UNKNOWN_EXPECTED_FIELD_TYPE',
                              'NUMBER',
                              'STRING',
                            ])
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            number: z.never().optional(),
                            list: z.never().optional(),
                          }),
                          z.object({
                            list: z.never().optional(),
                            number: z
                              .object({
                                value: z
                                  .number()
                                  .describe(
                                    'Numeric value to compare with the value of the specified field.'
                                  )
                                  .optional(),
                                operation: z
                                  .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                  .describe('Operation to use.')
                                  .optional(),
                              })
                              .describe(
                                'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                              ),
                          }),
                          z.object({
                            number: z.never().optional(),
                            list: z
                              .object({
                                values: z
                                  .array(z.string())
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe(
                                'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                              ),
                          }),
                        ])
                      )
                      .describe(
                        'Single condition that must be met for the rule to be applied to an order.'
                      ),
                  }),
                  z.object({
                    condition: z.never().optional(),
                    conditionTree:
                      comWixpressServiceFeesV1ConditionTreeSchema.describe(
                        'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  conditionOptions: z.never().optional(),
                  conditionTreeOptions: z.never().optional(),
                }),
                z.object({
                  conditionTreeOptions: z.never().optional(),
                  conditionOptions: z
                    .intersection(
                      z.object({
                        path: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        orderFieldPath: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        expectedType: z
                          .object({
                            value: z
                              .enum(['NUMBER', 'STRING'])
                              .describe(
                                'Type of the field specified in `orderFieldPath`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                        expectedFieldType: z
                          .enum([
                            'UNKNOWN_EXPECTED_FIELD_TYPE',
                            'NUMBER',
                            'STRING',
                          ])
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          number: z.never().optional(),
                          list: z.never().optional(),
                        }),
                        z.object({
                          list: z.never().optional(),
                          number: z
                            .object({
                              value: z
                                .number()
                                .describe(
                                  'Numeric value to compare with the value of the specified field.'
                                )
                                .optional(),
                              operation: z
                                .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                .describe('Operation to use.')
                                .optional(),
                            })
                            .describe(
                              'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                            ),
                        }),
                        z.object({
                          number: z.never().optional(),
                          list: z
                            .object({
                              values: z
                                .array(z.string())
                                .min(1)
                                .max(100)
                                .optional(),
                            })
                            .describe(
                              'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Single condition that must be met for the rule to be applied to an order.'
                    ),
                }),
                z.object({
                  conditionOptions: z.never().optional(),
                  conditionTreeOptions:
                    comWixpressServiceFeesV1ConditionTreeSchema.describe(
                      'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                    ),
                }),
              ])
            ),
            z.xor([
              z.object({
                customTaxRate: z.never().optional(),
                taxGroupId: z.never().optional(),
              }),
              z.object({
                taxGroupId: z.never().optional(),
                customTaxRate: z
                  .string()
                  .describe(
                    'Percentage value to apply as a custom tax rate. Range: [0-100].'
                  ),
              }),
              z.object({
                customTaxRate: z.never().optional(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID. Internal only.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
        )
      )
      .optional(),
    pagingMetadata: z
      .object({
        count: z
          .number()
          .int()
          .describe('Number of items returned in the response.')
          .optional()
          .nullable(),
        cursors: z
          .object({
            next: z
              .string()
              .describe('Cursor pointing to next page in the list of results.')
              .optional()
              .nullable(),
            prev: z
              .string()
              .describe(
                'Cursor pointing to previous page in the list of results.'
              )
              .optional()
              .nullable(),
          })
          .describe('Offset that was requested.')
          .optional(),
        hasNext: z
          .boolean()
          .describe(
            'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
          )
          .optional()
          .nullable(),
      })
      .describe('Paging metadata.')
      .optional(),
  });
})();
export const BulkCreateRulesRequest = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    rules: z.array(
      z.intersection(
        z.object({
          _id: z.string().describe('Rule ID.').optional().nullable(),
          locationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Rule name.')
            .max(50)
            .min(1)
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the rule was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the rule was updated.')
            .optional()
            .nullable(),
          taxRate: z
            .string()
            .describe(
              "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
            )
            .optional()
            .nullable(),
          conditionsType: z
            .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          conditionType: z
            .enum(['UNDEFINED_CONDITION_TYPE', 'CONDITION', 'CONDITION_TREE'])
            .optional(),
          enabled: z
            .boolean()
            .describe(
              'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
            )
            .optional()
            .nullable(),
          label: z
            .string()
            .describe(
              'DEPRECATED. Defines the app that the rule is connected to.'
            )
            .max(100)
            .min(1)
            .optional()
            .nullable(),
          appId: z
            .string()
            .describe('Defines the app that the rule is connected to.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          roundingStrategy: z.enum(['HALF_UP', 'HALF_EVEN']).optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Data Extensions')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
            )
            .optional(),
        }),
        z.intersection(
          z.intersection(
            z.intersection(
              z.intersection(
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ]),
                z.xor([
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z.never().optional(),
                  }),
                  z.object({
                    percentageFee: z.never().optional(),
                    fixedFee: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Fixed fee. Must hold a positive value.'),
                  }),
                  z.object({
                    fixedFee: z.never().optional(),
                    percentageFee: z
                      .string()
                      .describe(
                        "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  condition: z.never().optional(),
                  conditionTree: z.never().optional(),
                }),
                z.object({
                  conditionTree: z.never().optional(),
                  condition: z
                    .intersection(
                      z.object({
                        path: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        orderFieldPath: z
                          .string()
                          .describe(
                            'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                          )
                          .max(1000)
                          .optional(),
                        expectedType: z
                          .object({
                            value: z
                              .enum(['NUMBER', 'STRING'])
                              .describe(
                                'Type of the field specified in `orderFieldPath`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                        expectedFieldType: z
                          .enum([
                            'UNKNOWN_EXPECTED_FIELD_TYPE',
                            'NUMBER',
                            'STRING',
                          ])
                          .describe(
                            'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          number: z.never().optional(),
                          list: z.never().optional(),
                        }),
                        z.object({
                          list: z.never().optional(),
                          number: z
                            .object({
                              value: z
                                .number()
                                .describe(
                                  'Numeric value to compare with the value of the specified field.'
                                )
                                .optional(),
                              operation: z
                                .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                .describe('Operation to use.')
                                .optional(),
                            })
                            .describe(
                              'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                            ),
                        }),
                        z.object({
                          number: z.never().optional(),
                          list: z
                            .object({
                              values: z
                                .array(z.string())
                                .min(1)
                                .max(100)
                                .optional(),
                            })
                            .describe(
                              'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Single condition that must be met for the rule to be applied to an order.'
                    ),
                }),
                z.object({
                  condition: z.never().optional(),
                  conditionTree:
                    comWixpressServiceFeesV1ConditionTreeSchema.describe(
                      'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                    ),
                }),
              ])
            ),
            z.xor([
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions: z.never().optional(),
              }),
              z.object({
                conditionTreeOptions: z.never().optional(),
                conditionOptions: z
                  .intersection(
                    z.object({
                      path: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      orderFieldPath: z
                        .string()
                        .describe(
                          'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                        )
                        .max(1000)
                        .optional(),
                      expectedType: z
                        .object({
                          value: z
                            .enum(['NUMBER', 'STRING'])
                            .describe(
                              'Type of the field specified in `orderFieldPath`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                      expectedFieldType: z
                        .enum([
                          'UNKNOWN_EXPECTED_FIELD_TYPE',
                          'NUMBER',
                          'STRING',
                        ])
                        .describe(
                          'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        number: z.never().optional(),
                        list: z.never().optional(),
                      }),
                      z.object({
                        list: z.never().optional(),
                        number: z
                          .object({
                            value: z
                              .number()
                              .describe(
                                'Numeric value to compare with the value of the specified field.'
                              )
                              .optional(),
                            operation: z
                              .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                              .describe('Operation to use.')
                              .optional(),
                          })
                          .describe(
                            'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                          ),
                      }),
                      z.object({
                        number: z.never().optional(),
                        list: z
                          .object({
                            values: z
                              .array(z.string())
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Single condition that must be met for the rule to be applied to an order.'
                  ),
              }),
              z.object({
                conditionOptions: z.never().optional(),
                conditionTreeOptions:
                  comWixpressServiceFeesV1ConditionTreeSchema.describe(
                    'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z.never().optional(),
            }),
            z.object({
              taxGroupId: z.never().optional(),
              customTaxRate: z
                .string()
                .describe(
                  'Percentage value to apply as a custom tax rate. Range: [0-100].'
                ),
            }),
            z.object({
              customTaxRate: z.never().optional(),
              taxGroupId: z
                .string()
                .describe('Tax group ID. Internal only.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
      )
    ),
    options: z
      .object({
        returnFullEntity: z
          .boolean()
          .describe(
            'Whether the full rule entity is returned. <br />\nDefault: `false`.'
          )
          .optional(),
      })
      .optional(),
  });
})();
export const BulkCreateRulesResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    results: z
      .array(
        z.object({
          itemMetadata: z
            .object({
              _id: z
                .string()
                .describe(
                  "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                )
                .optional()
                .nullable(),
              originalIndex: z
                .number()
                .int()
                .describe(
                  'Index of the item within the request array. Allows for correlation between request and response items.'
                )
                .optional(),
              success: z
                .boolean()
                .describe(
                  'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                )
                .optional(),
              error: z
                .object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                })
                .describe('Details about the error in case of failure.')
                .optional(),
            })
            .describe('Metadata of the rule.')
            .optional(),
          rule: z
            .intersection(
              z.object({
                _id: z.string().describe('Rule ID.').optional().nullable(),
                locationId: z
                  .string()
                  .describe(
                    'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Rule name.')
                  .max(50)
                  .min(1)
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Date and time the rule was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the rule was updated.')
                  .optional()
                  .nullable(),
                taxRate: z
                  .string()
                  .describe(
                    "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
                  )
                  .optional()
                  .nullable(),
                conditionsType: z
                  .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
                  .describe('Specifies the type of condition.')
                  .optional(),
                conditionType: z
                  .enum([
                    'UNDEFINED_CONDITION_TYPE',
                    'CONDITION',
                    'CONDITION_TREE',
                  ])
                  .describe('Specifies the type of condition.')
                  .optional(),
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^\d+$/, 'Must be a valid UInt64 string')
                  .describe(
                    'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
                  )
                  .optional()
                  .nullable(),
                label: z
                  .string()
                  .describe(
                    'DEPRECATED. Defines the app that the rule is connected to.'
                  )
                  .max(100)
                  .min(1)
                  .optional()
                  .nullable(),
                appId: z
                  .string()
                  .describe('Defines the app that the rule is connected to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                roundingStrategy: z
                  .enum(['HALF_UP', 'HALF_EVEN'])
                  .describe(
                    'Rounding strategy to apply to fee and tax calculation.'
                  )
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe('Data Extensions')
                  .optional(),
                tags: z
                  .object({
                    privateTags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                      )
                      .optional(),
                    tags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
                  )
                  .optional(),
              }),
              z.intersection(
                z.intersection(
                  z.intersection(
                    z.intersection(
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z.never().optional(),
                        }),
                        z.object({
                          percentageFee: z.never().optional(),
                          fixedFee: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ])
                    ),
                    z.xor([
                      z.object({
                        condition: z.never().optional(),
                        conditionTree: z.never().optional(),
                      }),
                      z.object({
                        conditionTree: z.never().optional(),
                        condition: z
                          .intersection(
                            z.object({
                              path: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              orderFieldPath: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              expectedType: z
                                .object({
                                  value: z
                                    .enum(['NUMBER', 'STRING'])
                                    .describe(
                                      'Type of the field specified in `orderFieldPath`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                              expectedFieldType: z
                                .enum([
                                  'UNKNOWN_EXPECTED_FIELD_TYPE',
                                  'NUMBER',
                                  'STRING',
                                ])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                number: z.never().optional(),
                                list: z.never().optional(),
                              }),
                              z.object({
                                list: z.never().optional(),
                                number: z
                                  .object({
                                    value: z
                                      .number()
                                      .describe(
                                        'Numeric value to compare with the value of the specified field.'
                                      )
                                      .optional(),
                                    operation: z
                                      .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                      .describe('Operation to use.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                  ),
                              }),
                              z.object({
                                number: z.never().optional(),
                                list: z
                                  .object({
                                    values: z
                                      .array(z.string())
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'Single condition that must be met for the rule to be applied to an order.'
                          ),
                      }),
                      z.object({
                        condition: z.never().optional(),
                        conditionTree:
                          comWixpressServiceFeesV1ConditionTreeSchema.describe(
                            'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                          ),
                      }),
                    ])
                  ),
                  z.xor([
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions: z.never().optional(),
                    }),
                    z.object({
                      conditionTreeOptions: z.never().optional(),
                      conditionOptions: z
                        .intersection(
                          z.object({
                            path: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            orderFieldPath: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            expectedType: z
                              .object({
                                value: z
                                  .enum(['NUMBER', 'STRING'])
                                  .describe(
                                    'Type of the field specified in `orderFieldPath`.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                            expectedFieldType: z
                              .enum([
                                'UNKNOWN_EXPECTED_FIELD_TYPE',
                                'NUMBER',
                                'STRING',
                              ])
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              number: z.never().optional(),
                              list: z.never().optional(),
                            }),
                            z.object({
                              list: z.never().optional(),
                              number: z
                                .object({
                                  value: z
                                    .number()
                                    .describe(
                                      'Numeric value to compare with the value of the specified field.'
                                    )
                                    .optional(),
                                  operation: z
                                    .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                    .describe('Operation to use.')
                                    .optional(),
                                })
                                .describe(
                                  'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                ),
                            }),
                            z.object({
                              number: z.never().optional(),
                              list: z
                                .object({
                                  values: z
                                    .array(z.string())
                                    .min(1)
                                    .max(100)
                                    .optional(),
                                })
                                .describe(
                                  'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Single condition that must be met for the rule to be applied to an order.'
                        ),
                    }),
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions:
                        comWixpressServiceFeesV1ConditionTreeSchema.describe(
                          'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z.never().optional(),
                  }),
                  z.object({
                    taxGroupId: z.never().optional(),
                    customTaxRate: z
                      .string()
                      .describe(
                        'Percentage value to apply as a custom tax rate. Range: [0-100].'
                      ),
                  }),
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z
                      .string()
                      .describe('Tax group ID. Internal only.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
            )
            .describe('The created rule.')
            .optional(),
        })
      )
      .optional(),
    bulkActionMetadata: z
      .object({
        totalSuccesses: z
          .number()
          .int()
          .describe('Number of items that were successfully processed.')
          .optional(),
        totalFailures: z
          .number()
          .int()
          .describe("Number of items that couldn't be processed.")
          .optional(),
        undetailedFailures: z
          .number()
          .int()
          .describe(
            'Number of failures without details because detailed failure threshold was exceeded.'
          )
          .optional(),
      })
      .describe('Bulk Create Rule metadata.')
      .optional(),
  });
})();
export const BulkUpdateRulesRequest = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    rules: z.array(
      z.object({
        rule: z
          .intersection(
            z.object({
              _id: z.string().describe('Rule ID.').optional().nullable(),
              locationId: z
                .string()
                .describe(
                  'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Rule name.')
                .max(50)
                .min(1)
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the rule was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the rule was updated.')
                .optional()
                .nullable(),
              taxRate: z
                .string()
                .describe(
                  "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
                )
                .optional()
                .nullable(),
              conditionsType: z
                .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
                .optional(),
              conditionType: z
                .enum([
                  'UNDEFINED_CONDITION_TYPE',
                  'CONDITION',
                  'CONDITION_TREE',
                ])
                .optional(),
              enabled: z
                .boolean()
                .describe(
                  'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^\d+$/, 'Must be a valid UInt64 string')
                .describe(
                  'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
                )
                .optional()
                .nullable(),
              label: z
                .string()
                .describe(
                  'DEPRECATED. Defines the app that the rule is connected to.'
                )
                .max(100)
                .min(1)
                .optional()
                .nullable(),
              appId: z
                .string()
                .describe('Defines the app that the rule is connected to.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              roundingStrategy: z.enum(['HALF_UP', 'HALF_EVEN']).optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Data Extensions')
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
                )
                .optional(),
            }),
            z.intersection(
              z.intersection(
                z.intersection(
                  z.intersection(
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Fixed fee. Must hold a positive value.'),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                          ),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        fixedFee: z.never().optional(),
                        percentageFee: z.never().optional(),
                      }),
                      z.object({
                        percentageFee: z.never().optional(),
                        fixedFee: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Fixed fee. Must hold a positive value.'),
                      }),
                      z.object({
                        fixedFee: z.never().optional(),
                        percentageFee: z
                          .string()
                          .describe(
                            "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                          ),
                      }),
                    ])
                  ),
                  z.xor([
                    z.object({
                      condition: z.never().optional(),
                      conditionTree: z.never().optional(),
                    }),
                    z.object({
                      conditionTree: z.never().optional(),
                      condition: z
                        .intersection(
                          z.object({
                            path: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            orderFieldPath: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            expectedType: z
                              .object({
                                value: z
                                  .enum(['NUMBER', 'STRING'])
                                  .describe(
                                    'Type of the field specified in `orderFieldPath`.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                            expectedFieldType: z
                              .enum([
                                'UNKNOWN_EXPECTED_FIELD_TYPE',
                                'NUMBER',
                                'STRING',
                              ])
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              number: z.never().optional(),
                              list: z.never().optional(),
                            }),
                            z.object({
                              list: z.never().optional(),
                              number: z
                                .object({
                                  value: z
                                    .number()
                                    .describe(
                                      'Numeric value to compare with the value of the specified field.'
                                    )
                                    .optional(),
                                  operation: z
                                    .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                    .describe('Operation to use.')
                                    .optional(),
                                })
                                .describe(
                                  'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                ),
                            }),
                            z.object({
                              number: z.never().optional(),
                              list: z
                                .object({
                                  values: z
                                    .array(z.string())
                                    .min(1)
                                    .max(100)
                                    .optional(),
                                })
                                .describe(
                                  'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Single condition that must be met for the rule to be applied to an order.'
                        ),
                    }),
                    z.object({
                      condition: z.never().optional(),
                      conditionTree:
                        comWixpressServiceFeesV1ConditionTreeSchema.describe(
                          'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    conditionOptions: z.never().optional(),
                    conditionTreeOptions: z.never().optional(),
                  }),
                  z.object({
                    conditionTreeOptions: z.never().optional(),
                    conditionOptions: z
                      .intersection(
                        z.object({
                          path: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          orderFieldPath: z
                            .string()
                            .describe(
                              'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                            )
                            .max(1000)
                            .optional(),
                          expectedType: z
                            .object({
                              value: z
                                .enum(['NUMBER', 'STRING'])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                          expectedFieldType: z
                            .enum([
                              'UNKNOWN_EXPECTED_FIELD_TYPE',
                              'NUMBER',
                              'STRING',
                            ])
                            .describe(
                              'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            number: z.never().optional(),
                            list: z.never().optional(),
                          }),
                          z.object({
                            list: z.never().optional(),
                            number: z
                              .object({
                                value: z
                                  .number()
                                  .describe(
                                    'Numeric value to compare with the value of the specified field.'
                                  )
                                  .optional(),
                                operation: z
                                  .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                  .describe('Operation to use.')
                                  .optional(),
                              })
                              .describe(
                                'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                              ),
                          }),
                          z.object({
                            number: z.never().optional(),
                            list: z
                              .object({
                                values: z
                                  .array(z.string())
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe(
                                'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                              ),
                          }),
                        ])
                      )
                      .describe(
                        'Single condition that must be met for the rule to be applied to an order.'
                      ),
                  }),
                  z.object({
                    conditionOptions: z.never().optional(),
                    conditionTreeOptions:
                      comWixpressServiceFeesV1ConditionTreeSchema.describe(
                        'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                      ),
                  }),
                ])
              ),
              z.xor([
                z.object({
                  customTaxRate: z.never().optional(),
                  taxGroupId: z.never().optional(),
                }),
                z.object({
                  taxGroupId: z.never().optional(),
                  customTaxRate: z
                    .string()
                    .describe(
                      'Percentage value to apply as a custom tax rate. Range: [0-100].'
                    ),
                }),
                z.object({
                  customTaxRate: z.never().optional(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID. Internal only.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
          )
          .describe('Rule to update.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    ),
    options: z
      .object({
        returnFullEntity: z
          .boolean()
          .describe(
            'Whether the full rule entity is returned. <br />\nDefault: `false`.'
          )
          .optional(),
      })
      .optional(),
  });
})();
export const BulkUpdateRulesResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    results: z
      .array(
        z.object({
          itemMetadata: z
            .object({
              _id: z
                .string()
                .describe(
                  "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                )
                .optional()
                .nullable(),
              originalIndex: z
                .number()
                .int()
                .describe(
                  'Index of the item within the request array. Allows for correlation between request and response items.'
                )
                .optional(),
              success: z
                .boolean()
                .describe(
                  'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                )
                .optional(),
              error: z
                .object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                })
                .describe('Details about the error in case of failure.')
                .optional(),
            })
            .describe('Metadata of the rule.')
            .optional(),
          rule: z
            .intersection(
              z.object({
                _id: z.string().describe('Rule ID.').optional().nullable(),
                locationId: z
                  .string()
                  .describe(
                    'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Rule name.')
                  .max(50)
                  .min(1)
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Date and time the rule was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the rule was updated.')
                  .optional()
                  .nullable(),
                taxRate: z
                  .string()
                  .describe(
                    "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
                  )
                  .optional()
                  .nullable(),
                conditionsType: z
                  .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
                  .describe('Specifies the type of condition.')
                  .optional(),
                conditionType: z
                  .enum([
                    'UNDEFINED_CONDITION_TYPE',
                    'CONDITION',
                    'CONDITION_TREE',
                  ])
                  .describe('Specifies the type of condition.')
                  .optional(),
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^\d+$/, 'Must be a valid UInt64 string')
                  .describe(
                    'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
                  )
                  .optional()
                  .nullable(),
                label: z
                  .string()
                  .describe(
                    'DEPRECATED. Defines the app that the rule is connected to.'
                  )
                  .max(100)
                  .min(1)
                  .optional()
                  .nullable(),
                appId: z
                  .string()
                  .describe('Defines the app that the rule is connected to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                roundingStrategy: z
                  .enum(['HALF_UP', 'HALF_EVEN'])
                  .describe(
                    'Rounding strategy to apply to fee and tax calculation.'
                  )
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe('Data Extensions')
                  .optional(),
                tags: z
                  .object({
                    privateTags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                      )
                      .optional(),
                    tags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
                  )
                  .optional(),
              }),
              z.intersection(
                z.intersection(
                  z.intersection(
                    z.intersection(
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z.never().optional(),
                        }),
                        z.object({
                          percentageFee: z.never().optional(),
                          fixedFee: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ])
                    ),
                    z.xor([
                      z.object({
                        condition: z.never().optional(),
                        conditionTree: z.never().optional(),
                      }),
                      z.object({
                        conditionTree: z.never().optional(),
                        condition: z
                          .intersection(
                            z.object({
                              path: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              orderFieldPath: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              expectedType: z
                                .object({
                                  value: z
                                    .enum(['NUMBER', 'STRING'])
                                    .describe(
                                      'Type of the field specified in `orderFieldPath`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                              expectedFieldType: z
                                .enum([
                                  'UNKNOWN_EXPECTED_FIELD_TYPE',
                                  'NUMBER',
                                  'STRING',
                                ])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                number: z.never().optional(),
                                list: z.never().optional(),
                              }),
                              z.object({
                                list: z.never().optional(),
                                number: z
                                  .object({
                                    value: z
                                      .number()
                                      .describe(
                                        'Numeric value to compare with the value of the specified field.'
                                      )
                                      .optional(),
                                    operation: z
                                      .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                      .describe('Operation to use.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                  ),
                              }),
                              z.object({
                                number: z.never().optional(),
                                list: z
                                  .object({
                                    values: z
                                      .array(z.string())
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'Single condition that must be met for the rule to be applied to an order.'
                          ),
                      }),
                      z.object({
                        condition: z.never().optional(),
                        conditionTree:
                          comWixpressServiceFeesV1ConditionTreeSchema.describe(
                            'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                          ),
                      }),
                    ])
                  ),
                  z.xor([
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions: z.never().optional(),
                    }),
                    z.object({
                      conditionTreeOptions: z.never().optional(),
                      conditionOptions: z
                        .intersection(
                          z.object({
                            path: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            orderFieldPath: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            expectedType: z
                              .object({
                                value: z
                                  .enum(['NUMBER', 'STRING'])
                                  .describe(
                                    'Type of the field specified in `orderFieldPath`.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                            expectedFieldType: z
                              .enum([
                                'UNKNOWN_EXPECTED_FIELD_TYPE',
                                'NUMBER',
                                'STRING',
                              ])
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              number: z.never().optional(),
                              list: z.never().optional(),
                            }),
                            z.object({
                              list: z.never().optional(),
                              number: z
                                .object({
                                  value: z
                                    .number()
                                    .describe(
                                      'Numeric value to compare with the value of the specified field.'
                                    )
                                    .optional(),
                                  operation: z
                                    .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                    .describe('Operation to use.')
                                    .optional(),
                                })
                                .describe(
                                  'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                ),
                            }),
                            z.object({
                              number: z.never().optional(),
                              list: z
                                .object({
                                  values: z
                                    .array(z.string())
                                    .min(1)
                                    .max(100)
                                    .optional(),
                                })
                                .describe(
                                  'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Single condition that must be met for the rule to be applied to an order.'
                        ),
                    }),
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions:
                        comWixpressServiceFeesV1ConditionTreeSchema.describe(
                          'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z.never().optional(),
                  }),
                  z.object({
                    taxGroupId: z.never().optional(),
                    customTaxRate: z
                      .string()
                      .describe(
                        'Percentage value to apply as a custom tax rate. Range: [0-100].'
                      ),
                  }),
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z
                      .string()
                      .describe('Tax group ID. Internal only.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
            )
            .describe('The created rule.')
            .optional(),
        })
      )
      .optional(),
    bulkActionMetadata: z
      .object({
        totalSuccesses: z
          .number()
          .int()
          .describe('Number of items that were successfully processed.')
          .optional(),
        totalFailures: z
          .number()
          .int()
          .describe("Number of items that couldn't be processed.")
          .optional(),
        undetailedFailures: z
          .number()
          .int()
          .describe(
            'Number of failures without details because detailed failure threshold was exceeded.'
          )
          .optional(),
      })
      .describe('Bulk Update Rule metadata.')
      .optional(),
  });
})();
export const BulkDeleteRulesRequest = z.object({
  ruleIds: z.array(z.string()),
});
export const BulkDeleteRulesResponse = (() => {
  let comWixpressServiceFeesV1ConditionTreeSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        operator: z
          .enum(['AND', 'OR'])
          .describe(
            'Specifies the logical operator to use when combining the evaluation of the left and right conditions.'
          )
          .optional(),
      }),
      z.intersection(
        z.xor([
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z.never().optional(),
          }),
          z.object({
            leftConditionsTree: z.never().optional(),
            leftCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            leftCondition: z.never().optional(),
            leftConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ]),
        z.xor([
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z.never().optional(),
          }),
          z.object({
            rightConditionsTree: z.never().optional(),
            rightCondition: z
              .intersection(
                z.object({
                  path: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  orderFieldPath: z
                    .string()
                    .describe(
                      'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                    )
                    .max(1000)
                    .optional(),
                  expectedType: z
                    .object({
                      value: z
                        .enum(['NUMBER', 'STRING'])
                        .describe(
                          'Type of the field specified in `orderFieldPath`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                  expectedFieldType: z
                    .enum(['UNKNOWN_EXPECTED_FIELD_TYPE', 'NUMBER', 'STRING'])
                    .describe(
                      'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    number: z.never().optional(),
                    list: z.never().optional(),
                  }),
                  z.object({
                    list: z.never().optional(),
                    number: z
                      .object({
                        value: z
                          .number()
                          .describe(
                            'Numeric value to compare with the value of the specified field.'
                          )
                          .optional(),
                        operation: z
                          .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                          .describe('Operation to use.')
                          .optional(),
                      })
                      .describe(
                        'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                      ),
                  }),
                  z.object({
                    number: z.never().optional(),
                    list: z
                      .object({
                        values: z.array(z.string()).min(1).max(100).optional(),
                      })
                      .describe(
                        'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Single condition that must be met for the rule to be applied to an order.'
              ),
          }),
          z.object({
            rightCondition: z.never().optional(),
            rightConditionsTree: z
              .lazy(() => comWixpressServiceFeesV1ConditionTreeSchema)
              .describe(
                'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
              ),
          }),
        ])
      )
    );
  return z.object({
    results: z
      .array(
        z.object({
          itemMetadata: z
            .object({
              _id: z
                .string()
                .describe(
                  "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                )
                .optional()
                .nullable(),
              originalIndex: z
                .number()
                .int()
                .describe(
                  'Index of the item within the request array. Allows for correlation between request and response items.'
                )
                .optional(),
              success: z
                .boolean()
                .describe(
                  'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                )
                .optional(),
              error: z
                .object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                })
                .describe('Details about the error in case of failure.')
                .optional(),
            })
            .describe('Metadata of the rule.')
            .optional(),
          rule: z
            .intersection(
              z.object({
                _id: z.string().describe('Rule ID.').optional().nullable(),
                locationId: z
                  .string()
                  .describe(
                    'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Rule name.')
                  .max(50)
                  .min(1)
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Date and time the rule was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the rule was updated.')
                  .optional()
                  .nullable(),
                taxRate: z
                  .string()
                  .describe(
                    "Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />\nMin: '0'. <br />\nMax: `100`."
                  )
                  .optional()
                  .nullable(),
                conditionsType: z
                  .enum(['NO_CONDITIONS', 'CONDITION', 'CONDITION_TREE'])
                  .describe('Specifies the type of condition.')
                  .optional(),
                conditionType: z
                  .enum([
                    'UNDEFINED_CONDITION_TYPE',
                    'CONDITION',
                    'CONDITION_TREE',
                  ])
                  .describe('Specifies the type of condition.')
                  .optional(),
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated.'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^\d+$/, 'Must be a valid UInt64 string')
                  .describe(
                    'Revision number. Increments by 1 each time the rule is updated.\nTo prevent conflicting changes, the existing `revision` must be used when updating a rule.'
                  )
                  .optional()
                  .nullable(),
                label: z
                  .string()
                  .describe(
                    'DEPRECATED. Defines the app that the rule is connected to.'
                  )
                  .max(100)
                  .min(1)
                  .optional()
                  .nullable(),
                appId: z
                  .string()
                  .describe('Defines the app that the rule is connected to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                roundingStrategy: z
                  .enum(['HALF_UP', 'HALF_EVEN'])
                  .describe(
                    'Rounding strategy to apply to fee and tax calculation.'
                  )
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe('Data Extensions')
                  .optional(),
                tags: z
                  .object({
                    privateTags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                      )
                      .optional(),
                    tags: z
                      .object({
                        tagIds: z.array(z.string()).max(100).optional(),
                      })
                      .describe(
                        'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules.'
                  )
                  .optional(),
              }),
              z.intersection(
                z.intersection(
                  z.intersection(
                    z.intersection(
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z.never().optional(),
                        }),
                        z.object({
                          percentageFee: z.never().optional(),
                          fixedFee: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Fixed fee. Must hold a positive value.'),
                        }),
                        z.object({
                          fixedFee: z.never().optional(),
                          percentageFee: z
                            .string()
                            .describe(
                              "Percentage fee. For example, `5` represents a 5% fee applied to the order's total."
                            ),
                        }),
                      ])
                    ),
                    z.xor([
                      z.object({
                        condition: z.never().optional(),
                        conditionTree: z.never().optional(),
                      }),
                      z.object({
                        conditionTree: z.never().optional(),
                        condition: z
                          .intersection(
                            z.object({
                              path: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              orderFieldPath: z
                                .string()
                                .describe(
                                  'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                                )
                                .max(1000)
                                .optional(),
                              expectedType: z
                                .object({
                                  value: z
                                    .enum(['NUMBER', 'STRING'])
                                    .describe(
                                      'Type of the field specified in `orderFieldPath`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                              expectedFieldType: z
                                .enum([
                                  'UNKNOWN_EXPECTED_FIELD_TYPE',
                                  'NUMBER',
                                  'STRING',
                                ])
                                .describe(
                                  'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                number: z.never().optional(),
                                list: z.never().optional(),
                              }),
                              z.object({
                                list: z.never().optional(),
                                number: z
                                  .object({
                                    value: z
                                      .number()
                                      .describe(
                                        'Numeric value to compare with the value of the specified field.'
                                      )
                                      .optional(),
                                    operation: z
                                      .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                      .describe('Operation to use.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                  ),
                              }),
                              z.object({
                                number: z.never().optional(),
                                list: z
                                  .object({
                                    values: z
                                      .array(z.string())
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                  })
                                  .describe(
                                    'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'Single condition that must be met for the rule to be applied to an order.'
                          ),
                      }),
                      z.object({
                        condition: z.never().optional(),
                        conditionTree:
                          comWixpressServiceFeesV1ConditionTreeSchema.describe(
                            'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                          ),
                      }),
                    ])
                  ),
                  z.xor([
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions: z.never().optional(),
                    }),
                    z.object({
                      conditionTreeOptions: z.never().optional(),
                      conditionOptions: z
                        .intersection(
                          z.object({
                            path: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            orderFieldPath: z
                              .string()
                              .describe(
                                'Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.'
                              )
                              .max(1000)
                              .optional(),
                            expectedType: z
                              .object({
                                value: z
                                  .enum(['NUMBER', 'STRING'])
                                  .describe(
                                    'Type of the field specified in `orderFieldPath`.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                            expectedFieldType: z
                              .enum([
                                'UNKNOWN_EXPECTED_FIELD_TYPE',
                                'NUMBER',
                                'STRING',
                              ])
                              .describe(
                                'Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              number: z.never().optional(),
                              list: z.never().optional(),
                            }),
                            z.object({
                              list: z.never().optional(),
                              number: z
                                .object({
                                  value: z
                                    .number()
                                    .describe(
                                      'Numeric value to compare with the value of the specified field.'
                                    )
                                    .optional(),
                                  operation: z
                                    .enum(['EQ', 'LT', 'LE', 'GT', 'GE'])
                                    .describe('Operation to use.')
                                    .optional(),
                                })
                                .describe(
                                  'Contains a numeric value and an operation.\n\nRequired if `expectedFieldType` is `NUMBER`.'
                                ),
                            }),
                            z.object({
                              number: z.never().optional(),
                              list: z
                                .object({
                                  values: z
                                    .array(z.string())
                                    .min(1)
                                    .max(100)
                                    .optional(),
                                })
                                .describe(
                                  'Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.\n\nRequired if `expectedFieldType` is `LIST`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Single condition that must be met for the rule to be applied to an order.'
                        ),
                    }),
                    z.object({
                      conditionOptions: z.never().optional(),
                      conditionTreeOptions:
                        comWixpressServiceFeesV1ConditionTreeSchema.describe(
                          'Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.'
                        ),
                    }),
                  ])
                ),
                z.xor([
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z.never().optional(),
                  }),
                  z.object({
                    taxGroupId: z.never().optional(),
                    customTaxRate: z
                      .string()
                      .describe(
                        'Percentage value to apply as a custom tax rate. Range: [0-100].'
                      ),
                  }),
                  z.object({
                    customTaxRate: z.never().optional(),
                    taxGroupId: z
                      .string()
                      .describe('Tax group ID. Internal only.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
            )
            .describe('The created rule.')
            .optional(),
        })
      )
      .optional(),
    bulkActionMetadata: z
      .object({
        totalSuccesses: z
          .number()
          .int()
          .describe('Number of items that were successfully processed.')
          .optional(),
        totalFailures: z
          .number()
          .int()
          .describe("Number of items that couldn't be processed.")
          .optional(),
        undetailedFailures: z
          .number()
          .int()
          .describe(
            'Number of failures without details because detailed failure threshold was exceeded.'
          )
          .optional(),
      })
      .describe('Bulk Delete Rule metadata.')
      .optional(),
  });
})();
export const BulkUpdateRuleTagsRequest = z.object({
  ruleIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the rules.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the rules.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateRuleTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated rule.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateRuleTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter that determines which rules to update tags for.'),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the rules.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the rules.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateRuleTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
